// ✅ hooks/useOrders.js
import { useState } from "react";

const API_URL = `${process.env.REACT_APP_SERVER_URI}/api/orders`;
const API_UPDATE_DB = `${process.env.REACT_APP_SERVER_URI}/api/orders/all`;

const useOrders = () => {
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [shopifyToken, setShopifyToken] = useState(null);
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [deliveryFilter, setDeliveryFilter] = useState("all");
  const [pushSummary, setPushSummary] = useState(null); // null when no summary


  const fetchOrders = async (page) => {
    try {
      if (!shopifyToken) return;
      const response = await fetch(`${API_URL}?page=${page}&limit=20`, {
        headers: { Authorization: shopifyToken }
      });
      const result = await response.json();
      setOrders(result.orders);
      setTotalPages(result.totalPages);
    } catch (err) {
      console.error("Error fetching orders:", err);
    }
  };

  const fetchAllOrderIds = async (page) => {
    try {

        const limit = 1000;
        const skip = (currentPage - 1) * 20; // Assuming each page shows 20 records

        const response = await fetch(`${API_URL}?limit=${limit}&skip=${skip}`, {
            headers: { Authorization: shopifyToken }
        });


        const result = await response.json();
  
        /*
        const filteredIds = result.orders
        .filter(order => {
          if (deliveryFilter === "all") return true;
          return order.fulfillments?.[0]?.shipment_status === deliveryFilter;
        })
        .map(order => order.id);*/

        const filteredIds = result.orders
        .filter(order => {
          if (deliveryFilter === "all") return true;
      
          const hasNoFulfillments = !Array.isArray(order.fulfillments) || order.fulfillments.length === 0;
          const shipmentStatus = order.fulfillments?.[0]?.shipment_status;
      
          const statusToCompare = hasNoFulfillments || shipmentStatus == null
            ? "pending"
            : shipmentStatus;
      
          return statusToCompare === deliveryFilter;
        })
        .map(order => order.id);
      
  
      setSelectedOrders(new Set(filteredIds));
    } catch (error) {
      console.error("Failed to fetch filtered order IDs:", error);
    }
  };
  


  const updateDatabase = async () => {
    try {
      setLoading(true);
      if (!shopifyToken) return alert("Login required");
      const response = await fetch(
        `${API_UPDATE_DB}?start=${encodeURIComponent(startDateTime)}&end=${encodeURIComponent(endDateTime)}`,
        { headers: { Authorization: shopifyToken } }
      );
      const result = await response.json();
      if (result?.success) {
        alert(result?.message);
        fetchOrders(currentPage);
      } else {
        alert("Error syncing DB");
      }
    } catch (err) {
      console.error("Error updating DB:", err);
    } finally {
      setLoading(false);
    }
  };

  const pushToZippie = async (id) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/pushToZippee/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: shopifyToken
        }
      });
      const result = await response.json();
      if (result.success) {
        const zippeeStatus = result.updateAcknowledgement?.updatedOrder?.zippee || "Unknown";
        setOrders((prev) => prev.map(o => o.id === id ? { ...o, zippee: zippeeStatus } : o));
        alert(`Order ${id} pushed to Zippee!`);
      } else {
        alert(`Failed to push order: ${result.message}`);
      }
    } catch (err) {
      console.error("Push error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* backend 
      return res.json({
      success: true,
      total: results.length,
      summary: results
    });
	
	 results.push({ id, success: false, message: error.message });*/

  const bulkPushToZippee = async () => {
    if (selectedOrders.size === 0) return alert("No orders selected");
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/pushToZippee/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: shopifyToken
        },
        body: JSON.stringify({ orderIds: Array.from(selectedOrders) })
      });
      const result = await response.json();
      if (result.success) {
        setPushSummary(result.summary || []);
        alert("✅ Bulk push complete!");
        fetchOrders(currentPage);
      } else {
        setPushSummary(result.summary || []);
        alert("❌ Some orders failed");
        console.error(result.summary);
      }
      console.log(pushSummary);

    } catch (err) {
      console.error("Bulk push error:", err);
      setPushSummary([{ id: "Error", message: err.message }]);
    } finally {
      setLoading(false);
      
    }
  };

  const toggleOrderSelection = (id) => {
    setSelectedOrders((prev) => {
      const updated = new Set(prev);
      updated.has(id) ? updated.delete(id) : updated.add(id);
      return updated;
    });
  };

  const isOrderSelected = (id) => selectedOrders.has(id);

  return {
    orders,
    totalPages,
    currentPage,
    setCurrentPage,
    fetchOrders,
    updateDatabase,
    pushToZippie,
    bulkPushToZippee,
    fetchAllOrderIds,
    selectedOrders,
    setSelectedOrders,
    toggleOrderSelection,
    isOrderSelected,
    loading,
    shopifyToken,
    setShopifyToken,
    startDateTime,
    setStartDateTime,
    endDateTime,
    setEndDateTime,
    deliveryFilter,
    setDeliveryFilter,
    pushSummary, 
    setPushSummary
  };
  
};

export default useOrders;
